/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Header,
  PaymentCard,
  PaymentBottomCard,
  DateFilterModal,
} from "../../components";
import { CompletedPayments, ChevronRight, Filter } from "../../assets/svgs";
import {
  getPaymentCardDate,
  getPaymentCardStatus,
  getPaymentCardStyles,
  getPaymentCardIcon,
  getOrdinal,
  filterPaymentsByDateRange,
} from "../../utils";
import { PAYMENT_CARD_STATUS, PAYMENT_STATUS, ROUTES } from "../../constants";
import useApp from "../../hooks/useApp";
import useNavigateWithQuery from "../../hooks/useNavigateWithQuery";
import "./styles.scss";

const Payments = () => {
  const navigate = useNavigateWithQuery();
  const location = useLocation();
  const { t } = useTranslation();
  const { financeDetails, getFinanceApplications } = useApp();
  const { financeApplicationId } = location.state || {};

  const [overduePayments, setOverduePayments] = useState([]);
  const [upcomingPayments, setUpcomingPayments] = useState([]);
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [dateRange, setDateRange] = useState({});

  const filterPaymentsByStatus = (installments) => {
    const overduePayments = installments?.filter((item) => {
      const status = getPaymentCardStatus({
        dueDateString: item.due_date,
        translate: t,
      });
      return status.key === PAYMENT_CARD_STATUS.OVERDUE;
    });
    setOverduePayments(overduePayments);

    const upcomingPayments = installments?.filter((item) => {
      const status = getPaymentCardStatus({
        dueDateString: item.due_date,
        translate: t,
      });
      return (
        status.key === PAYMENT_CARD_STATUS.DUE_TODAY ||
        status.key === PAYMENT_CARD_STATUS.DUE_IN_NTH_DAYS
      );
    });
    setUpcomingPayments(upcomingPayments);
  };

  const filteredOverduePayments = filterPaymentsByDateRange(
    overduePayments,
    dateRange
  );

  const filteredUpcomingPayments = filterPaymentsByDateRange(
    upcomingPayments,
    dateRange
  );

  useEffect(() => {
    const pendingInstallments = financeDetails?.installments?.filter(
      (installment) => installment.status !== PAYMENT_STATUS.PAID
    );
    filterPaymentsByStatus(pendingInstallments);
  }, [financeDetails]);

  useEffect(() => {
    getFinanceApplications(financeApplicationId);
  }, []);

  return (
    <div className="payments-container">
      <Header
        backEnable
        onClick={() => navigate(-1)}
        rightIcon={Filter}
        onClickRightIcon={() => setShowDateFilter(true)}
      />
      <div className="payments-content-container">
        <h1 className="payments-container-title">{t("payments.payments")}</h1>
        {filteredOverduePayments?.length ? (
          <p className="payments-section-heading">
            {t("payments.overdue_payments")}
          </p>
        ) : null}
        {filteredOverduePayments?.map((installment) => {
          const date = getPaymentCardDate(installment.due_date);
          const status = getPaymentCardStatus({
            dueDateString: installment.due_date,
            translate: t,
          });
          const cardStyles = getPaymentCardStyles(status);
          const cardIcon = getPaymentCardIcon(status);
          const ordinal = getOrdinal(installment.installment_number);
          return (
            <PaymentCard
              key={installment.id}
              dataContainerBgColor={cardStyles.dataContainerBgColor}
              statusContainerBgColor={cardStyles.statusContainerBgColor}
              dayTextColor={cardStyles.dayTextColor}
              monthTextColor={cardStyles.monthTextColor}
              paymentStatusTextColor={cardStyles.paymentStatusTextColor}
              dayText={date.day}
              monthText={date.month}
              paymentPeriodText={`${ordinal} ${t("payments.payment")}`}
              paymentAmountText={installment.formatted_amount_due}
              icon={cardIcon}
              paymentStatusText={status.label}
              isRounded={true}
            />
          );
        })}
        {filteredUpcomingPayments?.length ? (
          <p className="payments-section-heading">
            {t("payments.upcoming_payments")}
          </p>
        ) : null}
        {filteredUpcomingPayments?.map((installment) => {
          const date = getPaymentCardDate(installment.due_date);
          const status = getPaymentCardStatus({
            dueDateString: installment.due_date,
            translate: t,
          });
          const cardStyles = getPaymentCardStyles(status);
          const cardIcon = getPaymentCardIcon(status);
          const ordinal = getOrdinal(installment.installment_number);
          return (
            <PaymentCard
              key={installment.id}
              dataContainerBgColor={cardStyles.dataContainerBgColor}
              statusContainerBgColor={cardStyles.statusContainerBgColor}
              dayTextColor={cardStyles.dayTextColor}
              monthTextColor={cardStyles.monthTextColor}
              paymentStatusTextColor={cardStyles.paymentStatusTextColor}
              dayText={date.day}
              monthText={date.month}
              paymentPeriodText={`${ordinal} ${t("payments.payment")}`}
              paymentAmountText={installment.formatted_amount_due}
              icon={cardIcon}
              paymentStatusText={status.label}
              isRounded={true}
            />
          );
        })}
        <PaymentBottomCard
          icon={CompletedPayments}
          arrowIcon={ChevronRight}
          buttonText={t("payments.view_completed_payments")}
          btnVariant={"bg-neutral-50"}
          onClick={() =>
            navigate(`/${ROUTES.COMPLETED_PAYMENTS}`, {
              state: { financeApplicationId },
            })
          }
        />
      </div>

      <DateFilterModal
        visible={showDateFilter}
        onClickFilter={(dateRange) => {
          setDateRange(dateRange);
          setShowDateFilter(false);
        }}
        onClickReset={() => {
          setDateRange({});
        }}
        onClickBackdrop={() => {
          setShowDateFilter(false);
        }}
        fromDate={dateRange.fromDate}
        toDate={dateRange.toDate}
      />
    </div>
  );
};

export default Payments;
